import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Lock, Database, Eye, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { useShop } from '../context/ShopContext';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_API_URL;
const SHOP_NAME = process.env.REACT_APP_SHOP_NAME || 'Shop';

export default function PrivacyPage() {
    const { merchantId } = useShop();
    const [shopSettings, setShopSettings] = useState(null);
    const [contentMode, setContentMode] = useState('default');
    const [customContent, setCustomContent] = useState(null);
    const [customUrl, setCustomUrl] = useState(null);

    const fetchShopSettings = useCallback(async () => {
        if (!merchantId) return;
        try {
            const response = await axios.get(`${API_URL}/store/${merchantId}/settings`);
            setShopSettings(response.data);
            const mode = response.data.privacy_content_mode || 'default';
            setContentMode(mode);
            if (mode === 'url' && response.data.custom_privacy_url) {
                setCustomUrl(response.data.custom_privacy_url);
            } else if (mode === 'text' && response.data.privacy_content) {
                setCustomContent(response.data.privacy_content);
            }
        } catch (error) {
            console.error('Failed to fetch shop settings:', error);
        }
    }, [merchantId]);

    useEffect(() => {
        if (merchantId) fetchShopSettings();
    }, [merchantId, fetchShopSettings]);

    const displayName = shopSettings?.shop_name || SHOP_NAME;

    // Wait for shop context (merchantId from subdomain or env)
    if (!merchantId) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <Header />
                <main className="flex-1 flex items-center justify-center">
                    <p className="text-muted-foreground">Φόρτωση...</p>
                </main>
            </div>
        );
    }

    // If merchant has custom privacy URL, show redirect option
    if (contentMode === 'url' && customUrl) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Πίσω στο Shop
                    </Link>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl shadow-card p-8 md:p-12 text-center"
                    >
                        <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                            <Lock className="w-8 h-8 text-primary" />
                        </div>
                        <h1 className="font-display text-2xl font-bold text-primary mb-4">
                            Πολιτική Απορρήτου
                        </h1>
                        <p className="text-muted-foreground mb-6">
                            Το κατάστημα {displayName} έχει τη δική του Πολιτική Απορρήτου.
                        </p>
                        <a 
                            href={customUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Δείτε την Πολιτική Απορρήτου
                        </a>
                    </motion.div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Πίσω στο Shop
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl shadow-card p-8 md:p-12"
                >
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Lock className="w-7 h-7 text-primary" />
                        </div>
                        <div>
                            <h1 className="font-display text-3xl font-bold text-primary">
                                Πολιτική Απορρήτου
                            </h1>
                            <p className="text-muted-foreground text-sm mt-1">
                                {displayName}
                            </p>
                        </div>
                    </div>

                    {/* Quick Summary */}
                    <div className="grid md:grid-cols-3 gap-4 mb-10">
                        <div className="p-4 bg-blue-50 rounded-xl">
                            <Database className="w-6 h-6 text-blue-600 mb-2" />
                            <h4 className="font-medium text-blue-900">Τι Συλλέγουμε</h4>
                            <p className="text-sm text-blue-700">Μόνο τα απαραίτητα δεδομένα για τις παραγγελίες σας</p>
                        </div>
                        <div className="p-4 bg-green-50 rounded-xl">
                            <Eye className="w-6 h-6 text-green-600 mb-2" />
                            <h4 className="font-medium text-green-900">Πώς τα Χρησιμοποιούμε</h4>
                            <p className="text-sm text-green-700">Για την επεξεργασία και παράδοση των παραγγελιών</p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-xl">
                            <Shield className="w-6 h-6 text-purple-600 mb-2" />
                            <h4 className="font-medium text-purple-900">Πώς τα Προστατεύουμε</h4>
                            <p className="text-sm text-purple-700">Κρυπτογραφημένοι και ασφαλείς servers</p>
                        </div>
                    </div>

                    {contentMode === 'text' && customContent ? (
                        <div className="prose prose-gray max-w-none" dangerouslySetInnerHTML={{ __html: customContent }} />
                    ) : (
                        <div className="prose prose-gray max-w-none">
                            <p className="text-sm text-muted-foreground mb-6 border-l-4 border-primary/30 pl-4">
                                Η παρακάτω πολιτική απορρήτου είναι πρότυπο για το κατάστημα. Ο έμπορος μπορεί να ορίσει δικό του κείμενο από τις Ρυθμίσεις στο Dashboard.
                            </p>
                            <h2>1. Πληροφορίες που Συλλέγουμε</h2>
                            
                            <h3>Στοιχεία Παραγγελίας</h3>
                            <ul>
                                <li>Όνομα και στοιχεία επικοινωνίας</li>
                                <li>Διεύθυνση αποστολής</li>
                                <li>Email</li>
                                <li>Ιστορικό παραγγελιών</li>
                            </ul>

                            <h3>Στοιχεία Πληρωμής</h3>
                            <p>
                                Τα στοιχεία πληρωμής επεξεργάζονται με ασφάλεια από τους παρόχους πληρωμών μας (PayPal/Stripe).
                                Δεν αποθηκεύουμε αριθμούς πιστωτικών καρτών.
                            </p>

                            <h3>Αυτόματη Συλλογή</h3>
                            <ul>
                                <li>Διεύθυνση IP</li>
                                <li>Τύπος browser</li>
                                <li>Πληροφορίες συσκευής</li>
                                <li>Σελίδες που επισκέπτεστε</li>
                            </ul>

                            <h2>2. Πώς Χρησιμοποιούμε τις Πληροφορίες σας</h2>
                            <ul>
                                <li>Επεξεργασία και εκτέλεση παραγγελιών</li>
                                <li>Αποστολή επιβεβαιώσεων και ενημερώσεων</li>
                                <li>Απάντηση στα ερωτήματά σας</li>
                                <li>Βελτίωση του καταστήματος και των υπηρεσιών μας</li>
                                <li>Αποστολή marketing επικοινωνιών (με τη συγκατάθεσή σας)</li>
                            </ul>

                            <h2>3. Cookies</h2>
                            <p>
                                Χρησιμοποιούμε cookies για να θυμόμαστε τις προτιμήσεις σας και το καλάθι αγορών σας.
                                Μπορείτε να διαχειριστείτε τις προτιμήσεις cookies όταν επισκέπτεστε το site μας.
                            </p>

                            <h2>4. Κοινοποίηση Δεδομένων</h2>
                            <p>Μπορεί να μοιραστούμε τις πληροφορίες σας με:</p>
                            <ul>
                                <li><strong>Μεταφορείς</strong> - για να παραδώσουμε τις παραγγελίες σας</li>
                                <li><strong>Παρόχους πληρωμών</strong> - για να επεξεργαστούμε πληρωμές</li>
                                <li><strong>Νομικές αρχές</strong> - όταν απαιτείται από το νόμο</li>
                            </ul>
                            <p>Ποτέ δεν πουλάμε τις προσωπικές σας πληροφορίες.</p>

                            <h2>5. Ασφάλεια Δεδομένων</h2>
                            <p>
                                Χρησιμοποιούμε κρυπτογράφηση SSL και ασφαλείς servers για να προστατεύουμε τα δεδομένα σας.
                                Ωστόσο, καμία μετάδοση μέσω internet δεν είναι 100% ασφαλής.
                            </p>

                            <h2>6. Τα Δικαιώματά σας (GDPR)</h2>
                            <p>Έχετε το δικαίωμα να:</p>
                            <ul>
                                <li>Αποκτήσετε πρόσβαση στα προσωπικά σας δεδομένα</li>
                                <li>Διορθώσετε ανακριβή δεδομένα</li>
                                <li>Ζητήσετε διαγραφή των δεδομένων σας</li>
                                <li>Αποχωρήσετε από marketing emails</li>
                                <li>Εξάγετε τα δεδομένα σας</li>
                            </ul>

                            <h2>7. Διατήρηση Δεδομένων</h2>
                            <p>
                                Διατηρούμε τα δεδομένα σας για όσο χρόνο είναι απαραίτητο για την εκτέλεση παραγγελιών
                                και τη συμμόρφωση με νομικές υποχρεώσεις (συνήθως 7 χρόνια για φορολογικά αρχεία).
                            </p>

                            <h2>8. Επικοινωνία</h2>
                            <p>
                                Για ερωτήσεις σχετικά με το απόρρητο, επικοινωνήστε μαζί μας μέσω της{' '}
                                <Link to="/contact" className="text-primary hover:underline">σελίδας επικοινωνίας</Link>.
                            </p>
                        </div>
                    )}
                </motion.div>

                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <Link to="/terms" className="hover:text-primary">Όροι Χρήσης</Link>
                    <span className="mx-3">•</span>
                    <Link to="/" className="hover:text-primary">Αρχική</Link>
                </div>
            </main>
        </div>
    );
}
